import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import {
    useGetFilesQuery,
    useGetPrivateFilesQuery,
    useGetProjectQuery
} from '../../api/LabtraceApi';
import DashboardFilters from '../../components/DashboardFilters/DashboardFilters';
import DashboardHeader from '../../components/DashboardHeader/DashboardHeader';
import UploadForm from '../../components/Dropzone/UploadForm/UploadForm';
import FileOperationsMenu from '../../components/FilesOperations/FilesOperationsMenu';
import FilesGrid from '../../components/GridView/GridView';
import FilesTable from '../../components/TableView/TableView';
import { DialogContext } from '../../hooks/DialogContext';
import { FiltersContext } from '../../hooks/FiltersContext';
// import ProjectStatusDisplay from '../../components/ProjectStatusDisplay';

export const FileUploadProgressContext = createContext(null);
export const FileVerificationProgressContext = createContext(null);

// Styled drag overlay component
const DragOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    pointerEvents: 'none'
}));

// Styled container for drag & drop area
const DropContainer = styled(Box)(() => ({
    position: 'relative',
    minHeight: '400px'
}));

const ProjectFiles = () => {
    const headCells = [
        { id: 'name', label: 'File name' },
        { id: 'type', label: 'File type' },
        { id: 'owner', label: 'Owner' },
        { id: 'uploaded', label: 'Uploaded' },
        { id: 'status' },
        { id: 'options' }
    ];
    const [gridItems, setGridItems] = useState([]);
    const [tableItems, setTableItems] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const { view, activeFilter, setActiveFilter } = useContext(FiltersContext);
    const { projectId } = useParams();
    const { data: project } = useGetProjectQuery(projectId);
    const { path } = useRouteMatch();
    const isPrivateFolder = path.search(/private-folder/) > 0;
    const [isUploading, setIsUploading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    // Drag & Drop state
    const [isDragOver, setIsDragOver] = useState(false);
    const [isProhibitedType, setIsProhibitedType] = useState(false);
    const [dragCounter, setDragCounter] = useState(0);

    const { openDialog } = useContext(DialogContext);
    const user = useSelector((state) => state.user);

    // Allowed file types
    const allowedTypes = [
        'application/pdf',
        'image/png',
        'image/svg+xml',
        'image/jpeg',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    let files;
    let isLoading;
    let refetchFiles;
    if (isPrivateFolder) {
        const queryResponse = useGetPrivateFilesQuery(projectId);
        files = queryResponse.data;
        refetchFiles = queryResponse.refetch;
        isLoading = queryResponse.isLoading;
    } else {
        const queryResponse = useGetFilesQuery(projectId);
        files = queryResponse.data;
        refetchFiles = queryResponse.refetch;
        isLoading = queryResponse.isLoading;
    }

    // Check if user can upload (same logic as in DashboardFilters)
    const canUpload = project && (
        user.id === project.leaderId
        || project.members.records.some((record) => record.id === user.id && record.status === 'Active')
    );

    // Drag & Drop Event Handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canUpload) return;

        setDragCounter((prev) => prev + 1);
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDragOver(true);
            setIsProhibitedType(false);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canUpload) return;

        setDragCounter((prev) => prev - 1);
        if (dragCounter <= 1) {
            setIsDragOver(false);
            setIsProhibitedType(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canUpload) return;

        // Check file type during drag over if possible
        if (e.dataTransfer.types.includes('Files')) {
            setIsDragOver(true);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!canUpload) return;

        setIsDragOver(false);
        setDragCounter(0);

        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles.length > 0) {
            const file = droppedFiles[0];

            if (!allowedTypes.includes(file.type)) {
                setIsProhibitedType(true);
                setTimeout(() => setIsProhibitedType(false), 3000); // Hide error after 3 seconds
                return;
            }

            // Open upload dialog with the dropped file
            openDialog({
                isOpen: true,
                status: null,
                title: 'File uploading form',
                content: <UploadForm
                    fileData={file}
                    projectId={projectId}
                    isPrivateFolder={isPrivateFolder}
                    progressCallback={{ setIsUploading }}
                />
            });
        }
    };

    const handleCloseSnackbar = () => {
        setIsDownloading(false);
    };

    const action = (
        <Button color="secondary" size="small" onClick={handleCloseSnackbar}>
            HIDE
        </Button>
    );

    useEffect(() => {
        if (files) {
            if (activeFilter === 'primary') {
                const primaryGridFiles = files.filesGridData.filter((file) => !file.isSecondary);
                const primaryTableFiles = files.filesTableData.filter((file) => !file.isSecondary);

                setGridItems(primaryGridFiles);
                setTableItems(primaryTableFiles);
            } else if (activeFilter === 'secondary') {
                const primaryGridFiles = files.filesGridData.filter((file) => file.isSecondary);
                const primaryTableFiles = files.filesTableData.filter((file) => file.isSecondary);

                setGridItems(primaryGridFiles);
                setTableItems(primaryTableFiles);
            } else if (activeFilter === 'deleted') {
                const primaryGridFiles = files.filesGridData.filter((file) => file.status === 'Deleted');
                const primaryTableFiles = files.filesTableData.filter((file) => file.status === 'Deleted');

                setGridItems(primaryGridFiles);
                setTableItems(primaryTableFiles);
            } else if (activeFilter === 'all') {
                setGridItems(files.filesGridData);
                setTableItems(files.filesTableData);
            }
        }
    }, [files, activeFilter]);

    useEffect(() => {
        try {
            if (files && files.data) {
                refetchFiles();
            }
        } catch (e) {
            refetchFiles();
        }
    }, [files, isUploading]);

    const getTableItems = () => tableItems.map(({ isSecondary, ...item }) => ({
        ...item,
        options: {
            value: (
                project && item
                    ? <FileOperationsMenu project={project} file={item} openSnackbar={openSnackbar} setOpenSnackbar={setOpenSnackbar} />
                    : ''
            )
        }
    }));

    useEffect(() => {
        setActiveFilter('all');
    }, []);

    const useStyles = makeStyles(() => ({
        snackbarWidth: {
            width: '85%',
        }
    }));
    const classes = useStyles();

    const handleCloseUploadSnackbar = () => {
        setIsUploading(false);
    };

    const actionUpload = (
        <Button color="secondary" size="small" onClick={handleCloseUploadSnackbar}>
            HIDE
        </Button>
    );

    return (
        <FileUploadProgressContext.Provider value={{ setIsUploading }}>
            <DashboardHeader
                breadcrumbs={
                    [
                        { id: '1', name: 'Projects', href: '/dashboard/projects' },
                        { id: '2', name: project ? project.name : '', href: `/dashboard/projects/${projectId}` },
                        { id: '2', name: `${isPrivateFolder ? 'Private' : 'Public'} folder` }
                    ]
                }
                singleProject
            />

            {/* 📊 Project Status Polling Display */}
            {/* <ProjectStatusDisplay projectId={projectId} /> */}

            {/* <Button onClick={()=>refetchFiles()}>refetch</Button> */}
            <DashboardFilters files numberOfRecords={files ? files.filesGridData.length : 0} />

            <DropContainer
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                {(isDragOver || isProhibitedType) && (
                    <DragOverlay>
                        {!isProhibitedType ? (
                            <>
                                <ArrowCircleDownIcon sx={{ width: '80px', height: '80px', color: 'primary.main' }} />
                                <Typography variant="h6" color="primary.main" align="center" sx={{ mt: '1rem' }}>
                                    Drop files here to upload
                                </Typography>
                            </>
                        ) : (
                            <>
                                <CancelIcon sx={{ width: '80px', height: '80px', color: 'error.main' }} />
                                <Typography variant="h6" color="error.main" align="center" sx={{ mt: '1rem' }}>
                                    File type is not allowed
                                </Typography>
                            </>
                        )}
                    </DragOverlay>
                )}

                {
                    isLoading
                        ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 350px)' }}>
                                <CircularProgress />
                            </Box>
                        )
                        : view === 'grid'
                            ? <FilesGrid items={gridItems} type="files" isPrivate={isPrivateFolder} />
                            : (
                                <>
                                    <FilesTable rows={getTableItems()} headCells={headCells} />
                                    <Snackbar
                                        open={openSnackbar}
                                        autoHideDuration={6000}
                                        message={
                                            <div style={{ width: 500 }}>
                                                <Grid container spacing={2} sx={{ maxWidth: 500 }}>
                                                    <Grid item xs={4}>
                                                        Download in progress
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Box sx={{ width: '100%' }} mt={1}>
                                                            <LinearProgress />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </div>
                                        }
                                        classes={
                                            { root: classes.snackbarWidth }
                                        }
                                        action={action}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    />
                                </>
                            )
                }
            </DropContainer>

            <Snackbar
                open={isUploading}
                autoHideDuration={6000}
                message={
                    <div style={{ width: 500 }}>
                        <Grid container spacing={2} sx={{ maxWidth: 500 }}>
                            <Grid item xs={4}>
                                Upload in progress
                            </Grid>
                            <Grid item xs={8}>
                                <Box sx={{ width: '100%' }} mt={1}>
                                    <LinearProgress />
                                </Box>
                            </Grid>
                        </Grid>
                    </div>
                }
                action={actionUpload}
                classes={
                    { root: classes.snackbarWidth }
                }
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </FileUploadProgressContext.Provider>
    );
};

export default ProjectFiles;
